import { axiosClient } from '../../clients/axios-client'
import type {
  ApiChatMessage,
  ChatCompletionContext,
  MessageSource,
  ModelKey,
  ReviewCommentItem,
  ReviewMode,
} from '../../types'
import { DEFAULT_MODEL } from '../../types'
import { buildChatReviewRequestFields } from '../../types/review-mode'
import {
  type ReviewCommentDto,
  ChatCompletionRequestDtoSchema,
  ChatCompletionResponseDtoSchema,
  MessageSourcesDtoSchema,
  ReviewCommentsArrayDtoSchema,
} from './dtos/chat'

interface ChatCompletionResult {
  content: string
  isFinishError?: boolean
  comments?: ReviewCommentItem[]
  /** Empty array: no sources were used. Undefined: the backend sent sources we couldn't read. */
  sources?: MessageSource[]
}

interface ParsedSseEvent {
  contentChunk: string
  isDone: boolean
  // Present when the event contains a metadata object with finish_reason.
  finishReason?: string
  // Review mode: raw DTO comments extracted from the metadata chunk (transformed after schema validation)
  comments?: unknown
  sources?: unknown
}

const SUCCESSFUL_FINISH_REASONS = new Set(['stop', 'end_turn'])

/** The streaming endpoint answered with an HTTP error (the axios path gets AxiosError instead). */
export class ChatCompletionHttpError extends Error {
  status: number

  constructor(status: number) {
    super(`Streaming request failed: ${status}`)
    this.name = 'ChatCompletionHttpError'
    this.status = status
  }
}

function toReviewCommentItems(dtos: ReviewCommentDto[]): ReviewCommentItem[] {
  return dtos.map((dto) => {
    if (dto.comment_id) {
      return {
        kind: 'reply',
        reply: {
          commentId: dto.comment_id,
          commentText: dto.comment_text,
        },
      }
    }
    if (dto.paragraph_id) {
      return {
        kind: 'insert',
        comment: {
          paragraphId: dto.paragraph_id,
          searchText: dto.search_text ?? null,
          commentText: dto.comment_text,
        },
      }
    }
    return { kind: 'skipped' }
  })
}

function toMessageSources(payload: unknown): MessageSource[] | undefined {
  if (payload === undefined || payload === null) return []

  const parsed = MessageSourcesDtoSchema.safeParse(payload)
  if (!parsed.success) return undefined

  return parsed.data.map((dto) => ({
    fileId: dto.file_id,
    fileName: dto.filename,
    sections: Object.entries(dto.sections).map(([id, title]) => ({ id, title })),
  }))
}

function toChatCompletionResult({
  content,
  finishReason,
  comments,
  sources,
}: {
  content: string
  finishReason?: string
  comments?: unknown
  sources?: unknown
}): ChatCompletionResult {
  const isFinishError = finishReason !== undefined && !SUCCESSFUL_FINISH_REASONS.has(finishReason)
  const parsedComments = ReviewCommentsArrayDtoSchema.safeParse(comments)
  const reviewCommentItems = parsedComments.success ? toReviewCommentItems(parsedComments.data) : []
  const hasActionableComments = reviewCommentItems.some((item) => item.kind !== 'skipped')
  return {
    content,
    ...(isFinishError ? { isFinishError: true } : {}),
    ...(hasActionableComments ? { comments: reviewCommentItems } : {}),
    sources: toMessageSources(sources),
  }
}

function parseSseEvent(eventBlock: string): ParsedSseEvent {
  const lines = eventBlock.split(/\r?\n/)
  let contentChunk = ''
  let finishReason: string | undefined
  let comments: unknown
  let sources: unknown

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed.startsWith('data:')) continue

    // Supports both `data:foo` and `data: foo` forms
    const payload = trimmed.slice(5).trim()
    if (payload === '[DONE]') {
      return { contentChunk, isDone: true, finishReason, comments, sources }
    }

    try {
      const parsed = JSON.parse(payload) as {
        content?: unknown
        metadata?: { finish_reason?: string; comments?: unknown; sources?: unknown }
      }
      if (parsed.metadata !== undefined) {
        finishReason = parsed.metadata?.finish_reason ?? undefined
        comments = parsed.metadata?.comments ?? undefined
        sources = parsed.metadata?.sources ?? undefined
      }
      if (typeof parsed.content === 'string') {
        contentChunk += parsed.content
      }
    } catch {
      // Ignore malformed non-JSON SSE lines.
    }
  }

  return { contentChunk, isDone: false, finishReason, comments, sources }
}

interface PostChatCompletionParams {
  messages: ApiChatMessage[]
  model?: ModelKey
  context?: ChatCompletionContext
  reviewMode?: ReviewMode | null
  undoSignal?: AbortSignal
  streaming?: boolean
  authToken?: string | null
  onChunk?: (chunk: string) => void
}

function buildValidatedRequestBody({
  messages,
  model,
  context,
  reviewMode,
  stream,
}: {
  messages: ApiChatMessage[]
  model: ModelKey
  context: ChatCompletionContext
  reviewMode?: ReviewMode | null
  stream: boolean
}) {
  return ChatCompletionRequestDtoSchema.parse({
    messages,
    model,
    context,
    stream,
    ...buildChatReviewRequestFields(reviewMode ?? null),
  })
}

/**
 * Sends a chat completion request to the backend API.
 *
 * @param params - The request parameters
 * @param params.messages - Array of chat messages (can include system, user, and assistant messages)
 * @param params.model - Optional model to use (defaults to DEFAULT_MODEL)
 * @param params.context - Optional context object (defaults to empty object)
 * @param params.undoSignal - Optional AbortSignal to cancel the request if the user triggers an undo action
 * @param params.streaming - Whether to use streaming response (defaults to false)
 * @param params.authToken - Optional auth token for API requests (if not handled globally by axiosClient)
 * @param params.onChunk - Optional callback for each new content chunk received in streaming mode
 * @returns The chat completion response containing the assistant's message content
 *
 * TODO: Once GET /chat/models ships, create a `getModels` API function here
 * and a `useGetModels` query hook to populate model selection dynamically.
 */
export async function postChatCompletion({
  messages,
  model = DEFAULT_MODEL,
  context = {},
  reviewMode = null,
  undoSignal,
  streaming = false,
  authToken,
  onChunk,
}: PostChatCompletionParams): Promise<ChatCompletionResult> {
  const requestBody = buildValidatedRequestBody({
    messages,
    model,
    context,
    reviewMode,
    stream: streaming,
  })

  if (streaming) {
    return postChatCompletionStreaming({
      requestBody,
      undoSignal,
      authToken,
      onChunk,
    })
  }
  return postChatCompletionBlocking({ requestBody, undoSignal })
}

async function postChatCompletionBlocking({
  requestBody,
  undoSignal,
}: {
  requestBody: ReturnType<typeof buildValidatedRequestBody>
  undoSignal?: AbortSignal
}): Promise<ChatCompletionResult> {
  const { data } = await axiosClient.post('/chat/chat_completion', requestBody, {
    // LLM responses with large context windows can take 30-60s+
    timeout: 120000,
    signal: undoSignal,
  })

  // Validate response with Zod schema
  const parsedData = ChatCompletionResponseDtoSchema.parse(data)

  return toChatCompletionResult({
    content: parsedData.content,
    finishReason: parsedData.metadata?.finish_reason,
    comments: parsedData.metadata?.comments,
    sources: parsedData.metadata?.sources,
  })
}

async function postChatCompletionStreaming({
  requestBody,
  undoSignal,
  authToken,
  onChunk,
}: {
  requestBody: ReturnType<typeof buildValidatedRequestBody>
  undoSignal?: AbortSignal
  authToken?: string | null
  onChunk?: (chunk: string) => void
}): Promise<ChatCompletionResult> {
  const baseUrl = import.meta.env.VITE_API_URL
  if (!baseUrl) throw new Error('VITE_API_URL is not configured')

  const response = await fetch(`${baseUrl}/chat/chat_completion`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(requestBody),
    signal: undoSignal,
  })

  if (!response.ok) {
    throw new ChatCompletionHttpError(response.status)
  }

  if (!response.body) {
    throw new Error('Streaming response has no body')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullContent = ''
  let latestFinishReason: string | undefined
  let reviewCommentsPayload: unknown
  let sourcesPayload: unknown

  try {
    while (true) {
      if (undoSignal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError')
      }

      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // In SSE, an "event" is delimited by a blank line; an event can contain one or more lines.
      const events = buffer.split(/\r?\n\r?\n/)
      buffer = events.pop() ?? ''
      const shouldDebatchBurst = events.length > 1

      for (const event of events) {
        const parsedEvent = parseSseEvent(event)
        fullContent += parsedEvent.contentChunk

        // Track the latest finish_reason seen; metadata and [DONE] arrive as separate events.
        if (parsedEvent.finishReason !== undefined) {
          latestFinishReason = parsedEvent.finishReason
        }

        if (parsedEvent.comments !== undefined) {
          reviewCommentsPayload = parsedEvent.comments
        }

        if (parsedEvent.sources !== undefined) {
          sourcesPayload = parsedEvent.sources
        }

        if (onChunk && parsedEvent.contentChunk) {
          onChunk(parsedEvent.contentChunk)

          // If many SSE events were buffered into one network read,
          // yield so React can paint incremental bubble updates.
          if (shouldDebatchBurst) {
            await new Promise<void>((resolve) => {
              setTimeout(resolve, 0)
            })

            if (undoSignal?.aborted) {
              throw new DOMException('The operation was aborted.', 'AbortError')
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
  const parsedData = ChatCompletionResponseDtoSchema.parse({ content: fullContent })

  return toChatCompletionResult({
    content: parsedData.content,
    finishReason: latestFinishReason,
    comments: reviewCommentsPayload,
    sources: sourcesPayload,
  })
}
